import { type ClassValue, clsx } from 'clsx';
import { Apple, Cpu, Laptop, Monitor, Smartphone, Tablet } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { DropdownSelectOption } from '../components/common/DropdownSelect';
import { MenuItem, UserRole } from '../types';
import {
  AccommodationBooking,
  AccommodationBookingStatus,
} from '../types/accommodation';
import { getAppCurrencyPrefix } from './currency';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formattedRole = (role: UserRole) => {
  return role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (value) => value.toUpperCase());
};

// ─── Accommodation Helpers ─────────────────────────────────────────────────────────────────
export const STATUS_FILTERS: Array<{
  label: string;
  value: AccommodationBookingStatus | 'all';
}> = [
  { label: 'All Booking', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Checked in', value: 'checked_in' },
  { label: 'Checked out', value: 'checked_out' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'No show', value: 'no_show' },
];

export const shortDateTime = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function formatDateTime(dateStr: string, timeStr: string) {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}, ${timeStr.slice(0, 5)}`;
}

export function nightsBetween(checkIn: string, checkOut: string) {
  return Math.round(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000,
  );
}

export function guestLabel(b: { firstName?: string; lastName?: string }) {
  return b.firstName || b.lastName
    ? `${b.firstName} ${b.lastName}`.trim()
    : '—';
}

export function guestInitials({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}) {
  const f = firstName?.[0] ?? '';
  const l = lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

export function roomLabel(b: AccommodationBooking) {
  const rooms = b.booking_rooms ?? [];
  if (rooms.length === 0) return '—';
  const first = rooms[0];
  const typeName = first.room?.room_type?.name ?? 'Room';
  const roomNum = first.room?.room_number ?? '';
  if (rooms.length === 1) return `${typeName} #${roomNum}`;
  return `${typeName} #${roomNum}, +${rooms.length - 1}`;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function isoToDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Status badge ─────────────────────────────────────────────────────────────

export const STATUS_BADGE: Record<
  AccommodationBookingStatus,
  { label: string; classes: string }
> = {
  pending: {
    label: 'Pending',
    classes: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  confirmed: {
    label: 'Confirmed',
    classes: 'bg-blue-50 text-blue-700 border border-blue-200',
  },
  checked_in: {
    label: 'Checked in',
    classes: 'bg-green-50 text-green-700 border border-green-200',
  },
  checked_out: {
    label: 'Checked out',
    classes: 'bg-gray-100 text-gray-600 border border-gray-200',
  },
  cancelled: {
    label: 'Cancelled',
    classes: 'bg-red-50 text-red-600 border border-red-200',
  },
  no_show: {
    label: 'No show',
    classes: 'bg-orange-50 text-orange-600 border border-orange-200',
  },
};

// Avatar colour from initials
export const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-blue-500',
  'bg-green-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-pink-500',
];

export function avatarColor(name: string) {
  let code = 0;
  for (let i = 0; i < name.length; i += 1) code += name.charCodeAt(i);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ─── Guest preview helpers ────────────────────────────────────────────────────

export function formatGuestPreviewDate(
  iso: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '—';

  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options,
  });
}

export function formatGuestPreviewCurrency(
  amount: number,
  currency: string | null | undefined,
): string {
  const code = currency?.trim() || getAppCurrencyPrefix();
  return `${code} ${Math.round(amount).toLocaleString()}`;
}

export function getGuestPreviewInitials(
  firstName: string,
  lastName: string,
): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

export function formatGuestPreviewId(id: string): string {
  return `#${id.replace(/-/g, '').slice(0, 6).toUpperCase()}`;
}

export function formatGuestPreviewStatus(
  status: string | null | undefined,
): string {
  switch (status) {
    case 'checked_in':
      return 'Checked In';
    case 'checked_out':
      return 'Stayed';
    case 'no_show':
      return 'No Show';
    case 'cancelled':
      return 'Cancelled';
    case 'confirmed':
      return 'Confirmed';
    case 'pending':
      return 'Pending';
    default:
      return status ? status.replace(/_/g, ' ') : '—';
  }
}

export function getGuestPreviewStatusClasses(
  status: string | null | undefined,
): string {
  switch (status) {
    case 'checked_in':
    case 'checked_out':
      return 'bg-emerald-50 text-emerald-700';
    case 'confirmed':
      return 'bg-blue-50 text-blue-700';
    case 'pending':
      return 'bg-amber-50 text-amber-700';
    case 'cancelled':
    case 'no_show':
      return 'bg-red-50 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

export function getGuestPreviewTierClasses(
  tier: string | null | undefined,
): string {
  switch (tier) {
    case 'VIP':
      return 'bg-pos-primary/10 text-pos-primary';
    case 'Repeat Guest':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

export const cleanSearchString = (queryInput: string): string =>
  queryInput
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const prepareMenuItemsCSVData = (items: MenuItem[]) => {
  const currencyPrefix = getAppCurrencyPrefix();
  const formatPrice = (price: number) =>
    `${currencyPrefix} ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price)}`;
  const csvRows: string[] = [];
  items.forEach((item) => {
    const row = [
      `"${(item.name || '').replace(/"/g, '""')}"`,
      `"${(item.category_name || '').replace(/"/g, '""')}"`,
      `"${(item.department_name || '').replace(/"/g, '""')}"`,
      `"${formatPrice(item.base_price)}"`,
      item.status,
      item.is_taxable ? 'Yes' : 'No',
      `"${(item.sku || '').replace(/"/g, '""')}"`,
    ];
    csvRows.push(row.join(','));
  });

  return csvRows;
};

export const sanitizeFileSegment = (value: string): string =>
  value.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-');

export const DASHBOARD_REPORT_PAGE_SIZE = 20;

export const ALL_STATUSES_OPTION: DropdownSelectOption = {
  value: '',
  label: 'All statuses',
};

export const ORDER_STATUS_OPTIONS: DropdownSelectOption[] = [
  ALL_STATUSES_OPTION,
  { value: '9', label: 'OPEN' },
  { value: '1', label: 'CASH' },
  { value: '2', label: 'CARD' },
  { value: '3', label: 'MOBILE MONEY' },
  { value: '4', label: 'CHECK' },
  { value: '5', label: 'EFT' },
  { value: '6', label: 'CANCELLED' },
  { value: '7', label: 'NC' },
  { value: '8', label: 'PENDING' },
  { value: '10', label: 'CREDIT' },
];

export const ALL_DEPARTMENTS_OPTION: DropdownSelectOption = {
  value: '',
  label: 'All departments',
};

export const ALL_CLIENTS_OPTION: DropdownSelectOption = {
  value: '',
  label: 'All clients',
};

/**
 * Public routes that don't require authentication
 */
export const publicRoutePaths = ['/', '/onboarding', '/sales', '/review'];

// Routes a signed-out user may stay on. Deliberately excludes '/sales' (which
// is in publicRoutePaths only for the signed-in redirect logic) — a user whose
// session ended must always land back on the login page.
export const guestRoutePaths = ['/', '/onboarding', '/review'];

// ─── Consolidation of Dashboard Helpers ───

export function normalizeLabel(
  value: string | null | undefined,
  fallback: string,
): string {
  return value?.trim() || fallback;
}

export function getOSMetadata(osString: string) {
  const normalized = osString.toLowerCase();
  if (normalized.includes('mac') || normalized.includes('darwin')) {
    return {
      name: 'macOS',
      icon: Apple,
      color: 'text-gray-800 bg-gray-100 border-gray-200',
    };
  }
  if (normalized.includes('win')) {
    return {
      name: 'Windows',
      icon: Monitor,
      color: 'text-blue-700 bg-blue-50 border-blue-100',
    };
  }
  if (normalized.includes('android')) {
    return {
      name: 'Android',
      icon: Smartphone,
      color: 'text-green-700 bg-green-50 border-green-100',
    };
  }
  if (
    normalized.includes('ios') ||
    normalized.includes('ipad') ||
    normalized.includes('iphone')
  ) {
    return {
      name: 'iOS',
      icon: Tablet,
      color: 'text-indigo-700 bg-indigo-50 border-indigo-100',
    };
  }
  if (normalized.includes('linux')) {
    return {
      name: 'Linux',
      icon: Cpu,
      color: 'text-orange-700 bg-orange-50 border-orange-100',
    };
  }
  return {
    name: 'Other',
    icon: Laptop,
    color: 'text-slate-600 bg-slate-50 border-slate-200',
  };
}

export type AccountingDashboardPeriodPreset =
  | '7d'
  | '30d'
  | '90d'
  | 'month'
  | 'ytd'
  | 'year'
  | 'custom';

export function buildDashboardDateRange(
  preset: Exclude<AccountingDashboardPeriodPreset, 'custom'>,
) {
  const formatIsoDate = (date: Date) => date.toISOString().slice(0, 10);
  const today = new Date();
  const endDate = new Date(today);
  const startDate = new Date(today);

  switch (preset) {
    case '7d':
      startDate.setDate(today.getDate() - 6);
      break;
    case '30d':
      startDate.setDate(today.getDate() - 29);
      break;
    case '90d':
      startDate.setDate(today.getDate() - 89);
      break;
    case 'month':
      startDate.setDate(1);
      break;
    case 'ytd':
      startDate.setMonth(0, 1);
      break;
    case 'year':
      startDate.setMonth(0, 1);
      endDate.setMonth(11, 31);
      break;
    default:
      break;
  }

  return {
    dateFrom: formatIsoDate(startDate),
    dateTo: formatIsoDate(endDate),
  };
}

export type InventoryDashboardPeriodPreset =
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'this_month';

export function getInventoryDashboardRange(
  preset: InventoryDashboardPeriodPreset,
) {
  const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (preset) {
    case 'last_7_days': {
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      return { dateFrom: formatDateKey(start), dateTo: formatDateKey(end) };
    }
    case 'last_30_days': {
      const start = new Date(end);
      start.setDate(start.getDate() - 29);
      return { dateFrom: formatDateKey(start), dateTo: formatDateKey(end) };
    }
    case 'last_90_days': {
      const start = new Date(end);
      start.setDate(start.getDate() - 89);
      return { dateFrom: formatDateKey(start), dateTo: formatDateKey(end) };
    }
    case 'this_month':
    default: {
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return { dateFrom: formatDateKey(start), dateTo: formatDateKey(end) };
    }
  }
}
